import type { FunctionComponent, RefObject } from 'preact';
import { useContext, useMemo, useRef } from 'preact/hooks';

import type {
  TemplateContextMap,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { discardField } from 'Core/actions/request';
import { useCollapseFacet, useSwitcher } from 'Core/hooks/index';
import { createDialogOpenedSelector } from 'Core/selectors/dialog';
import { createSearchFacetHasSelectionSelector } from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import { type Facet, FacetValue, type FacetValueFull } from 'Models/index';
import facetsConfig from 'Models/uiConfig/facetsConfig';

import { FacetDialogName, FacetsPanelConfig } from './common/index';
import facetTypeToComponent, { type FacetTypeName } from './facet/index';
import Tooltip, { type TooltipTemplateParams } from './tooltip';

export type FacetConfig = ReturnType<typeof facetsConfig.getFacetConfig>;

export type CommonParams = {
  template: string;
  isCollapsed: boolean;
  selectedSize: number;
  tooltip: TemplateFunctionInvoker<TooltipTemplateParams>;
} & Omit<Facet, 'facetedSelection' | 'facetedValues' | 'getFilteredValues'>;

export type FacetCommonProps<TParams = TemplateContextMap> = {
  facet: Facet;
  field: string;
  selection: FacetValueFull[];
  config: FacetConfig;
  templateFunc: TemplateFunction<TParams>;
  facetRef: RefObject<HTMLElement>;
  commonParams: CommonParams;
  commonRoles: {
    toggleFacet: () => void;
    resetFacet: () => void;
    nextCase: () => void;
  };
  allowEqualBounds?: true;
};

type Props = {
  template: TemplateFunction<TemplateContextMap>;
  facet: Facet;
};

const BaseFacet: FunctionComponent<Props> = ({
  facet,
  facet: { field, name, selection, displayMode },
  template,
}) => {
  const dispatch = useDispatch();
  const hasSelectionSelector = useMemo(() => createSearchFacetHasSelectionSelector(facet), [facet]);
  const hasSelection = useSelector(hasSelectionSelector);
  const hadSelectionRef = useRef(hasSelection);

  const { disableCollapse, initCollapsed, facetPanelName, initExpandedFacets, initCollapsedFacets } =
    useContext(FacetsPanelConfig);

  const facetDialogOpened = useSelector(useMemo(() => createDialogOpenedSelector(FacetDialogName), []));
  const { switchCase, nextCase } = useSwitcher(facetsConfig.getSwitcherArgs(field, facetPanelName));
  const facetRef = useRef<HTMLElement>(null);
  const { isCollapsed, toggle: toggleFacet } = useCollapseFacet(
    facetRef,
    disableCollapse,
    !hasSelection &&
      ((initCollapsed && !initExpandedFacets?.includes(field)) || initCollapsedFacets?.includes(field)),
    hasSelection,
    hadSelectionRef,
  );
  const resetFacet = () => dispatch(discardField(field, { mayDiscardValue: true }));

  const tooltip = (templ: TemplateFunction<TooltipTemplateParams>) => {
    const props = { template: templ, name, field };
    return (<Tooltip {...props} key="tooltip" />) as TemplateResult;
  };

  const nextCaseSizeFix = () => {
    nextCase();
    if (hasSelection) {
      resetFacet();
    }
  };

  const isInFacetDialog = facetDialogOpened && facetPanelName === FacetDialogName;
  const config = facetsConfig.getFacetConfig(displayMode, field, facetPanelName, switchCase, isInFacetDialog);

  const commonProps: FacetCommonProps = {
    facet,
    field,
    selection,
    config,
    templateFunc: template,
    facetRef,
    commonParams: {
      ...facet,
      name: FacetValue.translateFacetTitle(field, name),
      template: config.name,
      isCollapsed,
      selectedSize: facet.facetedSelection.length,
      tooltip,
    },
    commonRoles: {
      toggleFacet,
      resetFacet,
      nextCase: nextCaseSizeFix,
    },
  };

  const Facet = facetTypeToComponent[
    config.type as FacetTypeName
  ] as FunctionComponent<FacetCommonProps> | null;
  return Facet ? <Facet {...commonProps} /> : null;
};

export default BaseFacet;
